import React from 'react';
import { FormControlLabel, FormControlLabelClassKey } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import TableDatePicker from 'ui/TableInputs/TableDatePicker/TableDatePicker';
import { AmountInput, Select, SelectItem, Textarea } from 'ui/Input';
import Switch from 'ui/Switch/Switch';
import { IAccount } from 'store/accounts.model';
import { TransferFrequencyList } from 'utils/constants';
import { onlyNCharacters } from 'utils/formatters';
import { IFormValues } from './BetweenAccounts.model';

const renderSourceAccountField = (accounts: IAccount[], submitting: boolean) => (
  <Select name="sourceAccount" label="Source Account" disabled={submitting}>
    {accounts.map(({ accountNumber }) => (
      <SelectItem value={accountNumber} key={accountNumber}>
        {accountNumber}
      </SelectItem>
    ))}
  </Select>
);

const renderAmountField = (submitting: boolean, currency?: string) => (
  <AmountInput
    name="amount"
    label="Amount"
    currency={currency}
    disabled={submitting}
  />
);

const renderDestinationAccountField = (accounts: IAccount[], submitting: boolean) => (
  <Select name="destinationAccountNo" label="Destination Account" disabled={submitting}>
    {accounts.map(({ accountNumber }) => (
      <SelectItem value={accountNumber} key={accountNumber}>
        {accountNumber}
      </SelectItem>
    ))}
  </Select>
);

const renderNarrationField = (submitting: boolean) => (
  <Textarea
    name="narration"
    label="Narration"
    disabled={submitting}
    fieldProps={{
      parse: onlyNCharacters(25),
    }}
  />
);

export const renderTransferFieldsFrom = (values: IFormValues, accounts: IAccount[], submitting: boolean) => {
  const selectedSourceAccount = accounts.find((account) => account.accountNumber === values.sourceAccount);
  const amountField = renderAmountField(submitting, selectedSourceAccount?.currency);
  return (
    <>
      {renderSourceAccountField(accounts, submitting)}
      {amountField}
    </>
  );
};

export const renderScheduleControl = (
  toggleScheduleTransfer: () => void,
  toggleSchedule: boolean | null,
  classes: Partial<ClassNameMap<FormControlLabelClassKey>> | undefined,
) => (
  <FormControlLabel
    control={<Switch />}
    checked={toggleSchedule === null ? false : toggleSchedule}
    onChange={toggleScheduleTransfer}
    label="Schedule transfer for later"
    labelPlacement="end"
    classes={classes}
    name="scheduleTransfer"
  />
);

export const renderScheduleForm = (
  submitting: boolean,
  dateFilterChange: (name: string, value?: string | null | undefined) => void,
  handleClearDateInput: (name: string, value?: string | null | undefined) => void,
  values: IFormValues,
) => (
  <>
    <Select name="frequency" label="Frequency" disabled={submitting}>
      {TransferFrequencyList.map(({ label, value }) => (
        <SelectItem value={value} key={value}>
          {label}
        </SelectItem>
      ))}
    </Select>
    <TableDatePicker
      name="specialDate"
      label="Special Date"
      onChange={dateFilterChange}
      onClickClear={handleClearDateInput}
      disabled={submitting || values.frequency !== 'specialDate'}
    />
  </>
);

export const renderTransferFieldsTo = (
  accounts: IAccount[],
  values: IFormValues,
  submitting: boolean,
  toggleScheduleTransfer: () => void,
  toggleSchedule: boolean | null,
  classes: Partial<ClassNameMap<FormControlLabelClassKey>> | ClassNameMap<'label' | 'root'> | undefined,
  dateFilterChange: (name: string, value?: string | null | undefined) => void,
  handleClearDateInput: (name: string, value?: string | null | undefined) => void,
) => {
  const sourceAccount = accounts.find((account) => account.accountNumber === values?.sourceAccount);
  return (
    <>
      {renderDestinationAccountField(
        accounts.filter(
          ({ accountNumber, currency }) =>
            accountNumber !== values?.sourceAccount && currency === sourceAccount?.currency,
        ),
        submitting,
      )}
      {renderNarrationField(submitting)}
      {renderScheduleControl(toggleScheduleTransfer, toggleSchedule, classes)}
      {values.scheduleTransfer && renderScheduleForm(submitting, dateFilterChange, handleClearDateInput, values)}
    </>
  );
};
